import { Close as CloseIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  getEntityTypes,
  getMinistries,
  getState,
  getTANList,
  saveTanMaster,
  updateTanMAster,
  uploadCsiFile,
} from '../../api/adminTAN.js'
import UploadIcon from '../../assets/icons/UploadIcon'
import Layout from '../../components/layout/Layout'
import { useSidebar } from '../../contexts/SidebarContext'

import { getEntityList } from '../../api/GetEntityMasterApi.js'
import { buildTanMasterPayload } from '../../utils/payloadBuilderTAN.js'

import {
  getIndiaTanByUuid as mtGetIndiaTanByUuid,
  saveIndiaTanMaster as mtSaveIndiaTanMaster,
  updateIndiaTanMaster as mtUpdateIndiaTanMaster,
  uploadIndiaCsiFile as mtUploadIndiaCsiFile,
} from '@/multi-territory/mt-services/mt-tan-master-api'
import { z } from 'zod'
import { COUNTRY_LIST, DEFAULT_PAGE } from '../../constants/index.js'
import { getDisabledFields } from '../../constants/tanFormDisabled.js'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { tanFormSchema } from './tanFormSchema.js'

// Field-level Zod validation on blur

/**
 * India TIN (TAN) create/edit form.
 * @param {object} [props]
 * @param {string} [props.returnTo] Route to navigate to on cancel/save.
 * @param {string} [props.editId] TAN master UUID to edit (overrides URL :id).
 * @param {string} [props.presetEntityUuid] Preselected entity UUID (locks the dropdown).
 * @param {string} [props.presetEntityName] Preselected entity name (prefilled + disabled).
 * @param {string} [props.presetPan] Preselected PAN / unique identification number (prefilled + disabled).
 * @param {boolean} [props.embedded] When true, render only the form body (no Layout) and skip the internal entity list fetch.
 * @param {string} [props.presetCountryCode] Country code from the MT filter (added to the save payload).
 */
const TINFormIndiaNew = ({
  returnTo = '/admin?step=tan',
  editId,
  presetEntityUuid,
  presetEntityName,
  presetPan,
  presetCountryCode,
  embedded = false,
} = {}) => {
  const navigate = useNavigate()
  const params = useParams()
  // editId (from MT flow) takes precedence over the URL :id param
  const id = editId ?? params.id
  const isEdit = Boolean(id)
  const { isCollapsed } = useSidebar()
  const [isTanActive, setIsTanActive] = useState(true)
  const [formData, setFormData] = useState({
    // Entity Details
    entityName: '',
    permanentAccountNumber: '', // Will be prepopulated based on entity selection
    taxDeductionAccountNumber: '',
    branchDivision: '', // New field
    country: 'INDIA', // Country/Region - mandatory, default INDIA
    flatDoorBlockNumber: '',
    nameOfPremisesBuilding: '',
    roadStreetLane: '',
    areaLocality: '',
    townDistrictCity: '',
    state: '',
    pinCode: '',
    stdCode: '',
    stdCodeAlternate: '',
    telephoneNumber: '',
    telephoneNumberAlternative: '',
    mobileNo: '',
    faxNo: '',
    entityEmailId: '',
    entityEmailIdAlternative: '',
    gstnNumber: '',
    entityType: '',
    paoCode: '',
    paoRegistrationNumber: '',
    ddoCode: '',
    ddoRegistrationNumber: '',
    ministryName: '',
    stateMinistry: '',
    ministryNameOther: '',
    ainNumber: '',

    // Responsible Person Details
    nameOfResponsiblePerson: '',
    flatDoorBlockNumberRP: '',
    nameOfPremisesBuildingRP: '',
    roadStreetLaneRP: '',
    areaLocalityRP: '',
    townDistrictCityRP: '',
    stateRP: '',
    pinCodeRP: '',
    designation: '',
    responsibleMobileNumber: '',
    stdCodeRP: '',
    telephoneNoRP: '',
    stdCodeAlternateRP: '',
    telephoneNoAlternateRP: '',
    emailRP: '',
    emailAlternateRP: '',
    pan: '',
    addressSameAsAbove: false,
  })

  const [errors, setErrors] = useState({})
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  // CSI file upload logic removed
  const [stateOptions, setStates] = useState([])
  const [ministryOptions, setMinistries] = useState([])
  const [entityTypeOptions, setEntityTypes] = useState([])
  const [entities, setEntities] = useState([])
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [existingFileName, setExistingFileName] = useState(null)
  const [touched, setTouched] = useState({})
  const { showSnackbar } = useSnackbar()

  // Helper function to determine if an error should be displayed
  const shouldShowError = fieldName => {
    // Only show errors for fields that have been touched (interacted with)
    // This prevents validation errors from showing on untouched fields
    return touched[fieldName] && errors[fieldName]
  }

  useEffect(() => {
    // When embedded in the MT flow the entity is preselected and passed in,
    // so skip the internal entity-master/list fetch entirely.
    if (embedded) return
    const fetchEntities = async () => {
      const payload = {
        pageFilter: {
          currentPage: 1,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: [],
      }
      try {
        const response = await getEntityList(payload)
        setEntities(response.data.data.content)
      } catch (error) {
        console.error('Error fetching entity list')
      }
    }

    fetchEntities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded])

  // When launched from the MT flow with a preselected entity, prefill the
  // entity name + PAN (unique identification number) and lock the dropdown.
  useEffect(() => {
    if (presetEntityUuid) {
      setSelectedEntity(presetEntityUuid)
    }
    if (presetEntityName || presetPan) {
      setFormData(prev => ({
        ...prev,
        entityName: presetEntityName ?? prev.entityName,
        permanentAccountNumber: presetPan ?? prev.permanentAccountNumber,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetEntityUuid, presetEntityName, presetPan])

  const fetchTanData = async () => {
    setLoading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: 1,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'taxDeductionAccountNumber',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: [],
      }

      // In the embedded MT flow, fetch the India TAN detail by tan uuid
      // (GET /wht-mt-admin/api/v1/tan-master/india/{uuid}) — mirrors the MT
      // TAN form. The legacy admin flow keeps using getTANList.
      let newData = []
      if (embedded) {
        const res = await mtGetIndiaTanByUuid(id)
        const payloadData = res.success ? res.data : null
        if (Array.isArray(payloadData)) {
          newData = payloadData
        } else if (Array.isArray(payloadData?.content)) {
          newData = payloadData.content
        } else if (payloadData && typeof payloadData === 'object') {
          newData = [payloadData]
        }
      } else {
        const response = await getTANList(payload)
        newData = response?.content || []
      }

      const filteredData =
        newData.find(
          item =>
            item.tanMasterUuid === id ||
            item.uuid === id ||
            item.responsiblePersonDetailsResponseDTO?.tanMasterUuid === id ||
            item.responsiblePersonDetailsRequestDTO?.tanMasterUuid === id
        ) || (newData.length === 1 ? newData[0] : undefined)

      if (!filteredData) return

      // find entity object
      const selectedEntityObj = entities.find(
        entity => entity.uuid === filteredData.entityMasterUuid
      )
      setIsTanActive(filteredData.active)
      // Extract responsible person data
      // Legacy India list API returns `...ResponseDTO`; the MT India detail API
      // (GET /tan-master/india/{uuid}) returns `...RequestDTO`. Support both.
      const responsiblePersonData =
        filteredData.responsiblePersonDetailsResponseDTO ||
        filteredData.responsiblePersonDetailsRequestDTO ||
        {}

      // build final formData with correct field mapping
      const finalFormData = {
        // Main entity data

        entityName: selectedEntityObj?.entityName || filteredData.entityName,
        permanentAccountNumber:
          selectedEntityObj?.entityPan ||
          filteredData.permanentAccountNumber ||
          filteredData.uniqueIdentificationNumber,
        taxDeductionAccountNumber:
          filteredData.taxDeductionAccountNumber ||
          filteredData.taxIdentificationNumber,
        branchDivision: filteredData.branchDivision,
        country: filteredData.country || 'INDIA',
        entityType: filteredData.entityType,

        // Entity address fields - these map to the main entity in the form
        flatDoorBlockNumber: filteredData.flatDoorBlockNumber,
        nameOfPremisesBuilding: filteredData.nameOfPremisesBuilding,
        roadStreetLane: filteredData.roadStreetLane,
        areaLocality: filteredData.areaLocality,
        townDistrictCity: filteredData.townDistrictCity,
        state: filteredData.state,
        pinCode: filteredData.pinCode,

        // Entity contact fields
        stdCode: filteredData.stdCode,
        telephoneNumber: filteredData.telephoneNumber,
        stdCodeAlternate: filteredData.stdCodeAlternative, // Note: form uses 'stdCodeAlternate'
        telephoneNumberAlternative: filteredData.telephoneNumberAlternative,
        entityEmailId: filteredData.entityEmailId,
        entityEmailIdAlternative: filteredData.entityEmailIdAlternative,

        // Other entity fields
        gstnNumber: filteredData.gstnNumber,
        paoCode: filteredData.paoCode,
        paoRegistrationNumber: filteredData.poaRegistration, // API: 'poaRegistration' -> Form: 'paoRegistrationNumber'
        ddoCode: filteredData.ddoCode,
        ddoRegistrationNumber: filteredData.ddoRegistrationNumber,
        ministryName: filteredData.ministryName,
        ministryNameOther: filteredData.ministryNameOther,
        stateMinistry: filteredData.stateName, // API: 'stateName' -> Form: 'stateMinistry'
        ainNumber: filteredData.ainNumber,

        // Responsible person fields
        nameOfResponsiblePerson: responsiblePersonData.nameOfResponsiblePerson,
        pan: responsiblePersonData.pan,
        designation: responsiblePersonData.designation,
        responsibleMobileNumber: responsiblePersonData.responsibleMobileNumber,

        // Responsible person address fields
        flatDoorBlockNumberRP: responsiblePersonData.flatDoorBlockNumber,
        nameOfPremisesBuildingRP: responsiblePersonData.nameOfPremises, // API: 'nameOfPremises' -> Form: 'nameOfPremisesBuildingRP'
        roadStreetLaneRP: responsiblePersonData.roadStreetLane,
        areaLocalityRP: responsiblePersonData.areaLocality,
        townDistrictCityRP: responsiblePersonData.townDistrictCity,
        stateRP: responsiblePersonData.state,
        pinCodeRP: responsiblePersonData.pincode, // API: 'pincode' -> Form: 'pinCodeRP'

        // Responsible person contact fields
        stdCodeRP: responsiblePersonData.stdCode,
        telephoneNoRP: responsiblePersonData.telephoneNumber,
        stdCodeAlternateRP: responsiblePersonData.stdCodeAlternative,
        telephoneNoAlternateRP:
          responsiblePersonData.telephoneNumberAlternative,
        emailRP: responsiblePersonData.email,
        emailAlternateRP: responsiblePersonData.emailAlternative,

        // Additional fields for edit mode
        mainObjID: filteredData.uuid,
        responsiblePersonUuid: responsiblePersonData.uuid,
        tanMasterUuid: filteredData.uuid, // Added this for payload builder
        addressSameAsAbove: false,
      }

      // The API returns null for empty fields, but the form's Zod schema
      // expects strings — coerce null/undefined to '' (other values, including
      // booleans like addressSameAsAbove, pass through unchanged).
      const sanitizedFormData = Object.fromEntries(
        Object.entries(finalFormData).map(([key, value]) => [
          key,
          value === null || value === undefined ? '' : value,
        ])
      )

      setFormData(sanitizedFormData)

      // Select the entity in the dropdown. In embedded MT edit mode the local
      // `entities` list isn't loaded, so fall back to the entity uuid returned
      // by the API (entityUuid / entityMasterUuid) so the name renders.
      const resolvedEntityUuid =
        selectedEntityObj?.uuid ||
        filteredData.entityUuid ||
        filteredData.entityMasterUuid
      if (resolvedEntityUuid) {
        setSelectedEntity(resolvedEntityUuid)
      }
    } catch (error) {
      console.error('Error fetching TAN data:')
    } finally {
      setLoading(false)
    }
  }
  const handleFieldBlur = (field, value) => {
    const formattedValue = value ?? ''

    // Mark only the current field as touched
    setTouched(prev => ({ ...prev, [field]: true }))

    try {
      // Create a partial schema for the specific field only
      const fieldSchema = tanFormSchema.pick({ [field]: true })

      // Validate only the specific field with only its value
      fieldSchema.parse({ [field]: formattedValue })

      // Clear error for this field only if validation passes
      setErrors(prev => ({ ...prev, [field]: '' }))
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Set error only for this specific field
        const errorMessage = error.issues[0]?.message || 'Validation error'
        setErrors(prev => ({ ...prev, [field]: errorMessage }))
      }
    }
  }

  useEffect(() => {
    if (isEdit && id && (embedded || entities.length > 0)) {
      fetchTanData()
    }
  }, [isEdit, id, entities])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statesData, ministriesData, entityTypesData] = await Promise.all(
          [getState(), getMinistries(), getEntityTypes()]
        )

        // Set the state with the fetched data
        setStates(statesData)
        setMinistries(ministriesData)
        setEntityTypes(entityTypesData)
      } catch (error) {
        console.error('Failed to fetch data:')
      }
    }

    fetchData() // Call the async function inside useEffect
  }, [])

  const handleChange = (field, value) => {
    const formattedValue = value ?? ''
    // Build the new form data first
    const newData = {
      ...formData,
      [field]: formattedValue,
    }

    // Handle entity type changes
    if (field === 'entityType') {
      const disabledFields = getDisabledFields(formattedValue)

      // Clear values of disabled fields
      Object.keys(disabledFields).forEach(fieldName => {
        if (disabledFields[fieldName]) {
          newData[fieldName] = ''
        }
      })

      // Clear errors for disabled fields
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        Object.keys(disabledFields).forEach(fieldName => {
          if (disabledFields[fieldName]) delete newErrors[fieldName]
        })
        return newErrors
      })
    }

    // Handle Ministry Name changes - Clear Ministry Name Other if not "OTHERS"
    if (field === 'ministryName') {
      if (value !== 'OTHERS') {
        newData.ministryNameOther = ''
        // Clear error for ministryNameOther if it exists
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors }
          delete newErrors.ministryNameOther
          return newErrors
        })
      }
    }

    // Handle "Address same as above" - Copy Entity Details -> Responsible Person Details
    if (field === 'addressSameAsAbove') {
      if (value === true) {
        // Copy FROM Entity Details TO Responsible Person Details
        newData.flatDoorBlockNumberRP = formData.flatDoorBlockNumber
        newData.nameOfPremisesBuildingRP = formData.nameOfPremisesBuilding
        newData.roadStreetLaneRP = formData.roadStreetLane
        newData.areaLocalityRP = formData.areaLocality
        newData.townDistrictCityRP = formData.townDistrictCity
        newData.stateRP = formData.state
        newData.pinCodeRP = formData.pinCode
      } else {
        // Clear Responsible Person Details fields when unchecking "Address same as above"
        newData.flatDoorBlockNumberRP = ''
        newData.nameOfPremisesBuildingRP = ''
        newData.roadStreetLaneRP = ''
        newData.areaLocalityRP = ''
        newData.townDistrictCityRP = ''
        newData.stateRP = ''
        newData.pinCodeRP = ''
      }
    }

    // Update the state
    setFormData(newData)

    // Set Touched State ONLY for the current field being changed
    setTouched(prev => ({ ...prev, [field]: true }))

    // Perform real-time validation as user types
    try {
      // Create a partial schema for the specific field only
      const fieldSchema = tanFormSchema.pick({ [field]: true })

      // Validate only the specific field with only its value
      fieldSchema.parse({ [field]: formattedValue })

      // Clear error for this field only if validation passes
      setErrors(prev => ({ ...prev, [field]: '' }))
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Set error only for this specific field
        const errorMessage = error.issues[0]?.message || 'Validation error'
        setErrors(prev => ({ ...prev, [field]: errorMessage }))
      }
    }
  }

  // CSI file upload and validation logic removed

  const validateForm = (dataToValidate = formData) => {
    const result = tanFormSchema.safeParse(dataToValidate)

    if (!result.success) {
      const zodErrors = {}
      result.error.issues.forEach(err => {
        if (err.path && err.path.length > 0) {
          zodErrors[err.path[0]] = err.message
        }
      })
      setErrors(zodErrors)
      // Surface the first error in a snackbar — some failing fields (e.g. the
      // read-only, prefilled PAN) have no inline error, so without this the
      // submit silently does nothing.
      const firstIssue = result.error.issues[0]
      if (firstIssue) {
        const fieldLabel =
          firstIssue.path?.[0] === 'permanentAccountNumber'
            ? 'Permanent Account Number: '
            : ''
        showSnackbar(`${fieldLabel}${firstIssue.message}`, 'error')
      }
      return false
    }

    setErrors({})
    return true
  }

  const validateFileUpload = () => {
    if (isEdit) return true
    if (!uploadedFile && !existingFileName) {
      setErrors(prev => ({
        ...prev,
        csiFile: 'CSI file is required for TAN and Entity Name validation',
      }))
      return false
    }

    // Clear error if file exists
    setErrors(prev => ({
      ...prev,
      csiFile: '',
    }))
    return true
  }

  // Store original data for edit mode
  const originalDataRef = React.useRef(null)

  useEffect(() => {
    if (
      isEdit &&
      formData &&
      Object.keys(formData).length > 0 &&
      !originalDataRef.current
    ) {
      // Store the original data after it is fetched
      originalDataRef.current = { ...formData }
    }
  }, [isEdit, formData])

  const handleSubmit = async e => {
    e.preventDefault()

    // Mark that form submission has been attempted
    setHasSubmitted(true)

    // Mark all fields as touched only during form submission
    setTouched(
      Object.fromEntries(Object.keys(formData).map(key => [key, true]))
    )
    const isFormValid = validateForm()

    const isFileValid = validateFileUpload()

    if (!isFormValid || !isFileValid) {
      return
    }

    setLoading(true)

    const tanMasterData = buildTanMasterPayload(
      formData,
      selectedEntity,
      isEdit,
      isTanActive
    )

    try {
      if (isEdit) {
        if (embedded) {
          const res = await mtUpdateIndiaTanMaster(
            tanMasterData,
            formData.mainObjID
          )
          if (!res.success) {
            showSnackbar(res.error ?? 'Failed to update TAN', 'error')
            return
          }
        } else {
          await updateTanMAster(tanMasterData, formData.mainObjID)
        }
        showSnackbar('TAN updated successfully!', 'success')
        navigate(returnTo)
      } else {
        try {
          if (embedded) {
            // MT India save mirrors the legacy India save: the uploaded CSI
            // file reference (fileUuid) is a URL path segment, and the plain
            // tanMasterData is the request body.
            const res = await mtSaveIndiaTanMaster(
              tanMasterData,
              existingFileName
            )
            if (!res.success) {
              showSnackbar(res.error ?? 'Failed to save TAN', 'error')
              return
            }
          } else {
            await saveTanMaster(tanMasterData, existingFileName)
          }
          showSnackbar('TAN created successfully!', 'success')
          navigate(returnTo)
        } catch (err) {
          showSnackbar(err.details, 'error')
        }
      }
    } catch (error) {
      console.error('Error saving TAN:')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate(returnTo)
  }

  const handleEntitySelect = entityUuid => {
    const selectedEntityObj = entities.find(
      entity => entity.uuid === entityUuid
    )
    if (selectedEntityObj) {
      setFormData(prev => {
        return {
          ...prev,
          entityName: selectedEntityObj.entityName,
          permanentAccountNumber: selectedEntityObj.entityPan,
        }
      })
      setSelectedEntity(selectedEntityObj.uuid)
      // Clear entityName error if present
      setErrors(prev => ({ ...prev, entityName: undefined }))
    }
  }

  const handleFileUpload = async event => {
    const file = event.target.files[0]

    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase()

      if (fileExtension !== 'csi') {
        setUploadedFile(null)
        setErrors(prev => ({
          ...prev,
          csiFile: 'Only .csi files are allowed',
        }))
        return
      }

      setUploadedFile(file)
      setErrors(prev => ({
        ...prev,
        csiFile: '',
      }))

      // Clear any existing file name when new file is uploaded
      if (isEdit) {
        setExistingFileName(null)
      }

      try {
        const response = embedded
          ? await mtUploadIndiaCsiFile(file)
          : await uploadCsiFile(file)
        // set file name
        if (response.success) {
          setExistingFileName(response.data)
        } else if (embedded) {
          showSnackbar(response.error ?? 'Failed to upload CSI file', 'error')
        }
      } catch (error) {
        showSnackbar(error.message, 'error')
        console.error('Upload failed:')
      }
    }
  }

  const handleDeleteFile = () => {
    setUploadedFile(null)
    if (isEdit) {
      setExistingFileName(null)
    }
    // Clear any file-related errors
    if (errors.csiFile) {
      setErrors(prev => ({
        ...prev,
        csiFile: '',
      }))
    }
  }

  const Wrapper = embedded ? React.Fragment : Layout

  return (
    <Wrapper>
      <Box sx={{ pb: { xs: '140px', sm: '120px' } }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              mb: 3,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 500, fontSize: { xs: '18px', sm: '20px' } }}
            >
              {isEdit ? 'Edit TAN' : 'Create TAN'}
            </Typography>

            {isEdit && (
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <span>TAN is</span>
                {isTanActive ? (
                  <span className="px-2 py-1 text-sm bg-green-100 rounded text-[#197C37]">
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-1 text-sm bg-red-100 rounded text-[#197C37]">
                    InActive
                  </span>
                )}
                <StyledSwitch
                  checked={isTanActive}
                  onChange={() => setIsTanActive(!isTanActive)}
                />
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: 'var(--forbrand-error)',
              mb: 4,
              fontSize: '14px',
            }}
          >
            (*) Fields are mandatory
          </Typography> */}

          <form onSubmit={handleSubmit}>
            {/* Entity Details Section */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  fontSize: { xs: '16px', sm: '18px' },
                }}
              >
                Entity Details
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Row 1: Entity Name & Permanent Account Number */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Name of the Entity
                      <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                    </Typography>
                    <FormControl fullWidth required>
                      <Select
                        value={selectedEntity}
                        onChange={e => handleEntitySelect(e.target.value)}
                        onBlur={e =>
                          handleFieldBlur('entityName', e.target.value)
                        }
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          width: '245px',
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                          '& .MuiList-root': {
                            height: '200px',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        displayEmpty
                        // fullWidth
                        disabled={isEdit || Boolean(presetEntityUuid)}
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select an entity
                              </span>
                            ) // placeholder
                          }
                          return (
                            entities.find(entity => entity.uuid === selected)
                              ?.entityName ??
                            presetEntityName ??
                            formData.entityName
                          )
                        }}
                      >
                        {entities.map(entity => (
                          <MenuItem key={entity.uuid} value={entity.uuid}>
                            {entity.entityName}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.entityName && errors.entityName && (
                        <FormHelperText error sx={{ margin: 0 }}>
                          {errors.entityName}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Permanent Account Number
                      <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      value={formData.permanentAccountNumber}
                      onChange={e =>
                        handleChange('permanentAccountNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          backgroundColor: '#f5f5f5',
                          height: '40px',
                        },
                      }}
                      disabled={true}
                      error={!!shouldShowError('permanentAccountNumber')}
                      helperText={
                        shouldShowError('permanentAccountNumber')
                          ? errors.permanentAccountNumber
                          : ''
                      }
                      FormHelperTextProps={{ sx: { margin: 0 } }}
                    />
                  </Box>
                </Box>

                {/* Row 2: TAN Number & Branch/Division */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Tax Deduction Account Number
                      <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      value={formData.taxDeductionAccountNumber}
                      onChange={e =>
                        handleChange(
                          'taxDeductionAccountNumber',
                          e.target.value
                        )
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'taxDeductionAccountNumber',
                          e.target.value
                        )
                      }
                      error={!!shouldShowError('taxDeductionAccountNumber')}
                      helperText={shouldShowError('taxDeductionAccountNumber')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                          },
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter TAN"
                      disabled={isEdit}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Branch/Division
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      value={formData.branchDivision}
                      error={
                        !!(touched.branchDivision && errors.branchDivision)
                      }
                      helperText={
                        touched.branchDivision && errors.branchDivision
                      }
                      onChange={e =>
                        handleChange('branchDivision', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('branchDivision', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Branch/Division"
                    />
                  </Box>
                </Box>

                {/* Row: Country/Region & Flat/Door/Block No. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Country/Region
                      <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      select
                      disabled={true}
                      value={formData.country || 'INDIA'}
                      error={!!(touched.country && errors.country)}
                      helperText={touched.country && errors.country}
                      onChange={e => handleChange('country', e.target.value)}
                      onBlur={e => handleFieldBlur('country', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                      }}
                    >
                      {(COUNTRY_LIST || []).map(option => (
                        <MenuItem
                          key={option.value || option}
                          value={option.value || option}
                        >
                          {option.label || option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Flat/Door/Block No.
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.flatDoorBlockNumber}
                      error={
                        !!(
                          touched.flatDoorBlockNumber &&
                          errors.flatDoorBlockNumber
                        )
                      }
                      helperText={
                        touched.flatDoorBlockNumber &&
                        errors.flatDoorBlockNumber
                      }
                      onChange={e =>
                        handleChange('flatDoorBlockNumber', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('flatDoorBlockNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Flat/Door/Block No"
                    />
                  </Box>
                </Box>

                {/* Row 3: Post Office & Road/Street/Lane */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Post Office
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.nameOfPremisesBuilding}
                      error={
                        !!(
                          touched.nameOfPremisesBuilding &&
                          errors.nameOfPremisesBuilding
                        )
                      }
                      helperText={
                        touched.nameOfPremisesBuilding &&
                        errors.nameOfPremisesBuilding
                      }
                      onChange={e =>
                        handleChange('nameOfPremisesBuilding', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'nameOfPremisesBuilding',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Post Office"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Road/Street/Block/Sector
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.roadStreetLane}
                      onChange={e =>
                        handleChange('roadStreetLane', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('roadStreetLane', e.target.value)
                      }
                      error={
                        !!(touched.roadStreetLane && errors.roadStreetLane)
                      }
                      helperText={
                        touched.roadStreetLane && errors.roadStreetLane
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Road/Street/Block/Sector"
                    />
                  </Box>
                </Box>

                {/* Row 4: Area/Locality & Town/District/City */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Area/Locality
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.areaLocality}
                      error={!!(touched.areaLocality && errors.areaLocality)}
                      helperText={touched.areaLocality && errors.areaLocality}
                      onChange={e =>
                        handleChange('areaLocality', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('areaLocality', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Area/Locality"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      District
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.townDistrictCity}
                      error={
                        !!(touched.townDistrictCity && errors.townDistrictCity)
                      }
                      helperText={
                        touched.townDistrictCity && errors.townDistrictCity
                      }
                      onBlur={e =>
                        handleFieldBlur('townDistrictCity', e.target.value)
                      }
                      onChange={e =>
                        handleChange('townDistrictCity', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter District"
                    />
                  </Box>
                </Box>

                {/* Row 5: State & Pin Code */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      State
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: '277.5px' }}>
                      <Select
                        value={formData.state}
                        error={!!errors.state}
                        onChange={e => handleChange('state', e.target.value)}
                        onBlur={e => handleFieldBlur('state', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                          '& .MuiList-root': {
                            height: '200px',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        }}
                        displayEmpty
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select state
                              </span>
                            )
                          }
                          return selected
                        }}
                        fullWidth
                      >
                        {stateOptions.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {!!(touched.state && errors.state) && (
                        <FormHelperText error sx={{ margin: 0 }}>
                          {errors.state}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      PIN/ZIP Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.pinCode}
                      error={!!(touched.pinCode && errors.pinCode)}
                      helperText={touched.pinCode && errors.pinCode}
                      onChange={e => handleChange('pinCode', e.target.value)}
                      onBlur={e => handleFieldBlur('pinCode', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 6 },
                      }}
                      placeholder="Enter Pin Code"
                    />
                  </Box>
                </Box>

                {/* Row 6: Country Code & Number */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Country Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.stdCode}
                      error={!!(touched.stdCode && errors.stdCode)}
                      helperText={touched.stdCode && errors.stdCode}
                      onChange={e => handleChange('stdCode', e.target.value)}
                      onBlur={e => handleFieldBlur('stdCode', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 5 },
                      }}
                      placeholder="Enter Country Code"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Number
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.telephoneNumber}
                      error={
                        !!(touched.telephoneNumber && errors.telephoneNumber)
                      }
                      helperText={
                        touched.telephoneNumber && errors.telephoneNumber
                      }
                      onChange={e =>
                        handleChange('telephoneNumber', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('telephoneNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter Number"
                    />
                  </Box>
                </Box>

                {/* Row 7: Email ID & STD Code (Alternate) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Email ID
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.entityEmailId}
                      error={!!(touched.entityEmailId && errors.entityEmailId)}
                      helperText={touched.entityEmailId && errors.entityEmailId}
                      onChange={e =>
                        handleChange('entityEmailId', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('entityEmailId', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Email ID"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      STD Code (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.stdCodeAlternate}
                      error={
                        !!(touched.stdCodeAlternate && errors.stdCodeAlternate)
                      }
                      helperText={
                        touched.stdCodeAlternate && errors.stdCodeAlternate
                      }
                      onChange={e =>
                        handleChange('stdCodeAlternate', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('stdCodeAlternate', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 5 },
                      }}
                      placeholder="Enter STD Code Alternate"
                    />
                  </Box>
                </Box>

                {/* Row 8: Telephone No. (Alternate) & Email ID (Alternate) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Telephone No. (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.telephoneNumberAlternative}
                      error={
                        !!(
                          touched.telephoneNumberAlternative &&
                          errors.telephoneNumberAlternative
                        )
                      }
                      helperText={
                        touched.telephoneNumberAlternative &&
                        errors.telephoneNumberAlternative
                      }
                      onChange={e =>
                        handleChange(
                          'telephoneNumberAlternative',
                          e.target.value
                        )
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'telephoneNumberAlternative',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter Telephone No. Alternate"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Email ID (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.entityEmailIdAlternative}
                      error={
                        !!(
                          touched.entityEmailIdAlternative &&
                          errors.entityEmailIdAlternative
                        )
                      }
                      helperText={
                        touched.entityEmailIdAlternative &&
                        errors.entityEmailIdAlternative
                      }
                      onChange={e =>
                        handleChange('entityEmailIdAlternative', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'entityEmailIdAlternative',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Email Alternate"
                    />
                  </Box>
                </Box>

                {/* Row 9: GSTN & Entity Type */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Goods and Service Tax Number (GSTN)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.gstnNumber}
                      error={!!(touched.gstnNumber && errors.gstnNumber)}
                      helperText={touched.gstnNumber && errors.gstnNumber}
                      onChange={e => handleChange('gstnNumber', e.target.value)}
                      onBlur={e =>
                        handleFieldBlur('gstnNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 15 },
                      }}
                      placeholder="Enter GSTN"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Entity Type
                    </Typography>
                    <FormControl
                      fullWidth
                      error={!!errors.entityType}
                      sx={{ maxWidth: '277.5px' }}
                    >
                      <Select
                        value={formData.entityType}
                        onChange={e =>
                          handleChange('entityType', e.target.value)
                        }
                        onBlur={e =>
                          handleFieldBlur('entityType', e.target.value)
                        }
                        error={!!errors.entityType}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        }}
                        displayEmpty
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select Entity Type
                              </span>
                            )
                          }
                          return selected
                        }}
                        fullWidth
                      >
                        {entityTypeOptions.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.entityType && errors.entityType && (
                        <FormHelperText sx={{ margin: 0 }}>
                          {errors.entityType}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                {/* Row 10: PAO Code & PAO Registration No. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      PAO Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.paoCode}
                      error={!!(touched.paoCode && errors.paoCode)}
                      helperText={touched.paoCode && errors.paoCode}
                      onChange={e => handleChange('paoCode', e.target.value)}
                      onBlur={e => handleFieldBlur('paoCode', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&  .MuiOutlinedInput-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 20 },
                      }}
                      placeholder="Enter PAO Code"
                      disabled={getDisabledFields(formData.entityType).paoCode}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      PAO Registration No.
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.paoRegistrationNumber}
                      error={
                        !!(
                          touched.paoRegistrationNumber &&
                          errors.paoRegistrationNumber
                        )
                      }
                      helperText={
                        touched.paoRegistrationNumber &&
                        errors.paoRegistrationNumber
                      }
                      onChange={e =>
                        handleChange('paoRegistrationNumber', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('paoRegistrationNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&  .MuiOutlinedInput-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      placeholder="Enter PAO Registration No."
                      disabled={
                        getDisabledFields(formData.entityType)
                          .paoRegistrationNumber
                      }
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 7 },
                      }}
                    />
                  </Box>
                </Box>

                {/* Row 11: DDO Code & DDO Registration No. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      DDO Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.ddoCode}
                      onChange={e => handleChange('ddoCode', e.target.value)}
                      error={!!(touched.ddoCode && errors.ddoCode)}
                      helperText={touched.ddoCode && errors.ddoCode}
                      onBlur={e => handleFieldBlur('ddoCode', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&  .MuiOutlinedInput-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 20 },
                      }}
                      disabled={getDisabledFields(formData.entityType).ddoCode}
                      placeholder="Enter DDO Code"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      DDO Registration No.
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.ddoRegistrationNumber}
                      error={
                        !!(
                          touched.ddoRegistrationNumber &&
                          errors.ddoRegistrationNumber
                        )
                      }
                      helperText={
                        touched.ddoRegistrationNumber &&
                        errors.ddoRegistrationNumber
                      }
                      onChange={e =>
                        handleChange('ddoRegistrationNumber', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('ddoRegistrationNumber', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&  .MuiOutlinedInput-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      disabled={
                        getDisabledFields(formData.entityType)
                          .ddoRegistrationNumber
                      }
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 20 },
                      }}
                      placeholder="Enter DDO Registration No."
                    />
                  </Box>
                </Box>

                {/* Row 12: Ministry / Dept.Name & Ministry / Dept.Name others */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Ministry / Dept.Name
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: '277.5px' }}>
                      <Select
                        value={formData.ministryName}
                        onChange={e =>
                          handleChange('ministryName', e.target.value)
                        }
                        onBlur={e =>
                          handleFieldBlur('ministryName', e.target.value)
                        }
                        error={!!errors.ministryName}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        }}
                        disabled={
                          getDisabledFields(formData.entityType).ministryName
                        }
                        displayEmpty
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select Ministry
                              </span>
                            )
                          }
                          return selected
                        }}
                        fullWidth
                      >
                        {ministryOptions.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.ministryName && errors.ministryName && (
                        <FormHelperText
                          className="reddd !text-[#d32f2f]"
                          sx={{ margin: 0 }}
                        >
                          {errors.ministryName}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Ministry / Dept.Name others
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.ministryNameOther}
                      error={
                        !!(
                          touched.ministryNameOther && errors.ministryNameOther
                        )
                      }
                      helperText={
                        touched.ministryNameOther && errors.ministryNameOther
                      }
                      onChange={e =>
                        handleChange('ministryNameOther', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('ministryNameOther', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                        '&  .MuiOutlinedInput-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      disabled={
                        getDisabledFields(formData.entityType)
                          .ministryNameOther ||
                        formData.ministryName !== 'OTHERS'
                      }
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                      }}
                      placeholder="Enter Ministry / Dept.Name others"
                    />
                  </Box>
                </Box>

                {/* Row 13: State Name & AIN */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      State Name
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: '277.5px' }}>
                      <Select
                        value={formData.stateMinistry || ''}
                        onChange={e =>
                          handleChange('stateMinistry', e.target.value)
                        }
                        onBlur={e =>
                          handleFieldBlur('stateMinistry', e.target.value)
                        }
                        error={!!errors.stateMinistry}
                        displayEmpty
                        disabled={
                          getDisabledFields(formData.entityType).stateMinistry
                        }
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                          '&.Mui-disabled': { backgroundColor: '#f5f5f5' },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: { maxHeight: 200 },
                          },
                        }}
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select Ministry State
                              </span>
                            )
                          }
                          return selected
                        }}
                        fullWidth
                      >
                        {stateOptions.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.stateMinistry && errors.stateMinistry && (
                        <FormHelperText
                          className="reddd !text-[#d32f2f]"
                          sx={{ margin: 0 }}
                        >
                          {errors.stateMinistry}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Account Office Identification Number (AIN) of PAO/TO/CDDO
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.ainNumber}
                      error={!!(touched.ainNumber && errors.ainNumber)}
                      helperText={touched.ainNumber && errors.ainNumber}
                      onChange={e => handleChange('ainNumber', e.target.value)}
                      onBlur={e => handleFieldBlur('ainNumber', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter AIN"
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Responsible Person Details Section */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 2, sm: 0 },
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: { xs: '16px', sm: '18px' } }}
                >
                  Responsible Person Details
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.addressSameAsAbove}
                      onChange={e =>
                        handleChange('addressSameAsAbove', e.target.checked)
                      }
                      sx={{
                        '&.Mui-checked': {
                          color: 'var(--forbrand-color)',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: 'var(--forbrand-color)',
                        },
                        color: 'var(--forbrand-color)',
                      }}
                    />
                  }
                  label="Address same as above"
                  sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Row 1: Name of the Responsible Person & Flat/Door/Block No. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Name of the Responsible Person
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.nameOfResponsiblePerson}
                      onChange={e =>
                        handleChange('nameOfResponsiblePerson', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'nameOfResponsiblePerson',
                          e.target.value
                        )
                      }
                      error={
                        !!(
                          touched.nameOfResponsiblePerson &&
                          errors.nameOfResponsiblePerson
                        )
                      }
                      helperText={
                        touched.nameOfResponsiblePerson &&
                        errors.nameOfResponsiblePerson
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Responsible Person Name"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Flat/Door/Block No.
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.flatDoorBlockNumberRP}
                      error={
                        !!(
                          touched.flatDoorBlockNumberRP &&
                          errors.flatDoorBlockNumberRP
                        )
                      }
                      helperText={
                        touched.flatDoorBlockNumberRP &&
                        errors.flatDoorBlockNumberRP
                      }
                      onChange={e =>
                        handleChange('flatDoorBlockNumberRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('flatDoorBlockNumberRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Flat/Door/Block No."
                    />
                  </Box>
                </Box>

                {/* Row 2: Post Office & Road/Street/Block/SectorLane */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Post Office
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.nameOfPremisesBuildingRP}
                      error={
                        !!(
                          touched.nameOfPremisesBuildingRP &&
                          errors.nameOfPremisesBuildingRP
                        )
                      }
                      helperText={
                        touched.nameOfPremisesBuildingRP &&
                        errors.nameOfPremisesBuildingRP
                      }
                      onChange={e =>
                        handleChange('nameOfPremisesBuildingRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'nameOfPremisesBuildingRP',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Post Office"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Road/Street/Block/Sector
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.roadStreetLaneRP}
                      error={
                        !!(touched.roadStreetLaneRP && errors.roadStreetLaneRP)
                      }
                      helperText={
                        touched.roadStreetLaneRP && errors.roadStreetLaneRP
                      }
                      onChange={e =>
                        handleChange('roadStreetLaneRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('roadStreetLaneRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Road/Street/Block/Sector"
                    />
                  </Box>
                </Box>

                {/* Row 3: Area/Locality & Town/District/City */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Area/Locality
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.areaLocalityRP}
                      error={
                        !!(touched.areaLocalityRP && errors.areaLocalityRP)
                      }
                      helperText={
                        touched.areaLocalityRP && errors.areaLocalityRP
                      }
                      onChange={e =>
                        handleChange('areaLocalityRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('areaLocalityRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter Area/Locality"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      District
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.townDistrictCityRP}
                      error={
                        !!(
                          touched.townDistrictCityRP &&
                          errors.townDistrictCityRP
                        )
                      }
                      helperText={
                        touched.townDistrictCityRP && errors.townDistrictCityRP
                      }
                      onChange={e =>
                        handleChange('townDistrictCityRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('townDistrictCityRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 25 },
                      }}
                      placeholder="Enter District"
                    />
                  </Box>
                </Box>

                {/* Row 4: State & Pin Code */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      State
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: '277.5px' }}>
                      <Select
                        value={formData.stateRP}
                        error={!!errors.stateRP}
                        onChange={e => handleChange('stateRP', e.target.value)}
                        onBlur={e => handleFieldBlur('stateRP', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                            },
                          },
                        }}
                        displayEmpty
                        fullWidth
                        renderValue={selected => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#aaa' }}>
                                Select State
                              </span>
                            )
                          }
                          return selected
                        }}
                      >
                        {stateOptions.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {!!(touched.stateRP && errors.stateRP) && (
                        <FormHelperText error sx={{ margin: 0 }}>
                          {errors.stateRP}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      PIN/ZIP Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.pinCodeRP}
                      error={!!(touched.pinCodeRP && errors.pinCodeRP)}
                      helperText={touched.pinCodeRP && errors.pinCodeRP}
                      onChange={e => handleChange('pinCodeRP', e.target.value)}
                      onBlur={e => handleFieldBlur('pinCodeRP', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 6 },
                      }}
                      placeholder="Enter PIN/ZIP Code"
                    />
                  </Box>
                </Box>

                {/* Row 5: Designation & Responsible Person's Mobile No. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Designation
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.designation}
                      onChange={e =>
                        handleChange('designation', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('designation', e.target.value)
                      }
                      error={!!(touched.designation && errors.designation)}
                      helperText={touched.designation && errors.designation}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 20 },
                      }}
                      placeholder="Enter Designation"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Responsible Person's Mobile No
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.responsibleMobileNumber}
                      error={
                        !!(
                          touched.responsibleMobileNumber &&
                          errors.responsibleMobileNumber
                        )
                      }
                      helperText={
                        touched.responsibleMobileNumber &&
                        errors.responsibleMobileNumber
                      }
                      onChange={e =>
                        handleChange('responsibleMobileNumber', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'responsibleMobileNumber',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter Mobile No."
                    />
                  </Box>
                </Box>

                {/* Row 6: Country Code & Number */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Country Code
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.stdCodeRP}
                      error={!!(touched.stdCodeRP && errors.stdCodeRP)}
                      helperText={touched.stdCodeRP && errors.stdCodeRP}
                      onChange={e => handleChange('stdCodeRP', e.target.value)}
                      onBlur={e => handleFieldBlur('stdCodeRP', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 5 },
                      }}
                      placeholder="Enter Country Code"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Number
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.telephoneNoRP}
                      error={!!(touched.telephoneNoRP && errors.telephoneNoRP)}
                      helperText={touched.telephoneNoRP && errors.telephoneNoRP}
                      onChange={e =>
                        handleChange('telephoneNoRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('telephoneNoRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter Number"
                    />
                  </Box>
                </Box>

                {/* Row 7: STD Code (Alternate) & Telephone No. (Alternate) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      STD Code (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.stdCodeAlternateRP}
                      error={
                        !!(
                          touched.stdCodeAlternateRP &&
                          errors.stdCodeAlternateRP
                        )
                      }
                      helperText={
                        touched.stdCodeAlternateRP && errors.stdCodeAlternateRP
                      }
                      onChange={e =>
                        handleChange('stdCodeAlternateRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('stdCodeAlternateRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 5 },
                      }}
                      placeholder="Enter STD Code Alternate"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Telephone No. (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.telephoneNoAlternateRP}
                      error={
                        !!(
                          touched.telephoneNoAlternateRP &&
                          errors.telephoneNoAlternateRP
                        )
                      }
                      helperText={
                        touched.telephoneNoAlternateRP &&
                        errors.telephoneNoAlternateRP
                      }
                      onChange={e =>
                        handleChange('telephoneNoAlternateRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur(
                          'telephoneNoAlternateRP',
                          e.target.value
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter Telephone No. Alternate"
                    />
                  </Box>
                </Box>

                {/* Row 8: Email & Email (Alternate) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Email ID
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.emailRP}
                      error={!!(touched.emailRP && errors.emailRP)}
                      helperText={touched.emailRP && errors.emailRP}
                      onChange={e => handleChange('emailRP', e.target.value)}
                      onBlur={e => handleFieldBlur('emailRP', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Email"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Email ID (Alternate)
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.emailAlternateRP}
                      error={
                        !!(touched.emailAlternateRP && errors.emailAlternateRP)
                      }
                      helperText={
                        touched.emailAlternateRP && errors.emailAlternateRP
                      }
                      onChange={e =>
                        handleChange('emailAlternateRP', e.target.value)
                      }
                      onBlur={e =>
                        handleFieldBlur('emailAlternateRP', e.target.value)
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 75 },
                      }}
                      placeholder="Enter Email Alternate"
                    />
                  </Box>
                </Box>

                {/* Row 9: Permanent Account Number (single field) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: { xs: 'auto', sm: '200px', md: '250px' },
                        fontSize: '14px',
                        fontWeight: { xs: 500, sm: 400 },
                      }}
                    >
                      Permanent Account Number
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.pan}
                      error={!!(touched.pan && errors.pan)}
                      helperText={touched.pan && errors.pan}
                      onChange={e => handleChange('pan', e.target.value)}
                      onBlur={e => handleFieldBlur('pan', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          border: '1px solid #E7E7E7',
                          height: '40px',
                          fontSize: '14px',
                        },
                      }}
                      slotProps={{
                        formHelperText: {
                          sx: { margin: 0 },
                        },
                        htmlInput: { maxLength: 10 },
                      }}
                      placeholder="Enter PAN"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}></Box> {/* Empty space for alignment */}
                </Box>
              </Box>
            </Paper>
          </form>
        </Box>

        {/* Fixed Bottom Action Bar */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, lg: isCollapsed ? '66px' : '315px' },
            right: '0',
            backgroundColor: '#fff',
            boxShadow: {
              xs: '0 -4px 20px rgba(0,0,0,0.15)',
              sm: '0 -2px 10px rgba(0,0,0,0.1)',
            },
            borderTop: '1px solid #e0e0e0',
            zIndex: 1,
            transition: 'left 0.3s ease',
            maxHeight: { xs: '200px', sm: 'auto' },
            overflowY: { xs: 'auto', sm: 'visible' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              px: { xs: 2, sm: 3 },
              py: 2,
              // height: { xs: 'auto', lg: '64px' },
              gap: { xs: 2, sm: 0 },
            }}
          >
            {isEdit ? (
              <Box
                sx={{ flex: { xs: 'none', sm: 1 }, order: { xs: 2, sm: 1 } }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    Entity Name and TAN already validated with previously
                    uploaded CSI file
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{ flex: { xs: 'none', sm: 1 }, order: { xs: 2, sm: 1 } }}
              >
                {/* Show attached file if exists (edit mode or new upload) */}
                {uploadedFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px' },
                      }}
                    >
                      Attached File:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--forbrand-color)',
                        fontSize: { xs: '12px', sm: '14px' },
                        wordBreak: 'break-all',
                      }}
                    >
                      {uploadedFile ? uploadedFile.name : null}
                    </Typography>
                    <Button
                      onClick={handleDeleteFile}
                      sx={{
                        p: 0.5,
                        color: 'var(--forbrand-primary)',
                        minWidth: 'auto',
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '18px' }} />
                    </Button>
                  </Box>
                )}

                {/* File upload area - show when no file is attached */}
                {!uploadedFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                      border: errors.csiFile
                        ? '2px dashed #f44336'
                        : '2px dashed #ccc',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <UploadIcon />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        fontSize: { xs: '11px', sm: '14px' },
                        textAlign: { xs: 'center', sm: 'left' },
                        lineHeight: { xs: 1.3, sm: 1.4 },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ display: { xs: 'block', sm: 'inline' } }}
                      >
                        Drag & drop CSI file (mandatory for TAN and Entity Name
                        validation)
                      </Box>
                      <Box
                        component="span"
                        sx={{ display: { xs: 'block', sm: 'inline' } }}
                      >
                        or{' '}
                        <Button
                          component="label"
                          variant="text"
                          sx={{
                            textTransform: 'none',
                            p: 0,
                            minWidth: 'auto',
                            color: 'var(--forbrand-color)',
                            textDecoration: 'underline',
                            fontSize: { xs: '11px', sm: '14px' },
                            fontWeight: { xs: 600, sm: 400 },
                          }}
                        >
                          Browse Files
                          <input
                            type="file"
                            hidden
                            accept=".csi"
                            onChange={handleFileUpload}
                          />
                        </Button>
                      </Box>
                    </Typography>
                  </Box>
                )}

                {errors.csiFile && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#f44336',
                      mt: 1,
                      fontSize: { xs: '11px', sm: '12px' },
                    }}
                  >
                    {errors.csiFile}
                  </Typography>
                )}
              </Box>
            )}

            {/* Right side - Action buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 2 },
                ml: { xs: 0, sm: !isEdit ? 3 : 0 },
                order: { xs: 1, sm: 2 },
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                flexDirection: { xs: 'row', sm: 'row' },
              }}
            >
              <Button
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  minWidth: { xs: 'auto', sm: 100 },
                  flex: { xs: 1, sm: 'none' },
                  color: 'var(--forbrand-brand-varient)',
                  height: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: { xs: 500, sm: 400 },
                  border: { xs: '1px solid var(--forbrand-color)', sm: 'none' },
                  borderRadius: { xs: '8px', sm: '4px' },
                  '&:hover': {
                    backgroundColor: {
                      xs: 'rgba(217, 57, 84, 0.04)',
                      sm: 'transparent',
                    },
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  minWidth: { xs: 'auto', sm: 150 },
                  flex: { xs: 1, sm: 'none' },
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  height: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: { xs: 500, sm: 400 },
                  borderRadius: { xs: '8px', sm: '4px' },
                  boxShadow: {
                    xs: '0 2px 8px rgba(217, 57, 84, 0.3)',
                    sm: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                  '&:hover': {
                    boxShadow: {
                      xs: '0 4px 12px rgba(217, 57, 84, 0.4)',
                      sm: '0 4px 8px rgba(0,0,0,0.15)',
                    },
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading
                  ? 'Saving...'
                  : isEdit
                    ? 'Update Changes'
                    : 'Save & Upload'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Wrapper>
  )
}

export default TINFormIndiaNew
